const tooltipClassName = 'byl-tooltip';
const tooltipBtnCloseClassName = `${tooltipClassName}__close`;
const tooltipTextClassName = `${tooltipClassName}__text`;
const tooltipTitleClassName = `${tooltipClassName}__title`;
const tooltipButtonClassName = `${tooltipClassName}__button`;
const hideClass = 'd-none';
const getCloseBtn = (tooltip) => tooltip.querySelector(`.${tooltipBtnCloseClassName}`);
const getTextContainer = (tooltip) => tooltip.querySelector(`.${tooltipTextClassName}`);
const getTitleContainer = (tooltip) => tooltip.querySelector(`.${tooltipTitleClassName}`);
const getButtonContainer = (tooltip) => tooltip.querySelector(`.${tooltipButtonClassName}`);
const getAnalyticsEventCenter = require('../common/getAnalyticsEventCenter');
const { onOnce } = require('../common/utils/domEvents');
const { setCookie } = require('../common/utils/cookieUtils');

const analyticsEventCenter = getAnalyticsEventCenter();

let currentTooltip = null;

/**
 * It gets the tooltip state from session storage,
 * or returns an empty object if there's no tooltip state in session storage
 * @returns {Object} the value of the sessionStorage item 'byl-tooltip' parsed as JSON.
 * If the item does not exist, it returns an empty object.
 */
function getTooltipFromSession() {
  return JSON.parse(sessionStorage.getItem('byl-tooltip')) || {};
}

/**
 * It takes an object and saves it in the browser's session storage
 * @param {Object} tooltipState - The state of the tooltip.
 */
function saveTooltipInSession(tooltipState) {
  sessionStorage.setItem('byl-tooltip', JSON.stringify(tooltipState));
}

/**
 * Set cookie for tooltip with expired time age
 * @param {string} type - The type/naming of tooltip.
 * @param {number} age - Time for cookie to expire.
 */
function saveTooltipCookie({ type, cookieAge }) {
  setCookie(type, true, cookieAge);
}

/**
 * It gets the current state of the tooltip from the session, then it updates
 * the state of the tooltip to close the current tooltip, and finally it saves
 * the updated state of the tooltip in the session.
 * @param {string} type - The type/naming of tooltip.
 */
function saveCloseInSession(type) {
  const sessionTooltip = getTooltipFromSession();
  const tooltipState = {
    ...sessionTooltip,
    [type]: true,
  };
  saveTooltipInSession(tooltipState);
}

/**
 * If the user has not closed the tooltip before, then show the tooltip
 * @param {string} type - The type/naming of tooltip.
 * @returns {boolean} A boolean value.
 */
function shouldShowTooltip(type) {
  const sessionTooltip = getTooltipFromSession();
  return !sessionTooltip?.[type];
}

/**
 * It checks if cookie exists for showing/hiding tooltip
 * @param {string} type - The type/naming of tooltip.
 * @returns {boolean} true or false
 */
function shouldShowTooltipCookie({ type }) {
  if (document.cookie.indexOf(type) === -1) {
    return true;
  }

  return false;
}

/**
 * @typedef {object} TooltipColor
 * @property {string} [background] - The background color of tooltip.
 * @property {string} [text] - The text color of tooltip.
 * @property {string} [closeBtn] - The close button color of tooltip.
 */

/**
 * Set tooltip styles color
 * @param {HTMLElement} tooltip - The tooltip selector.
 * @param {TooltipColor} color - The options of tooltip to create.
 */
function setTooltipColor(tooltip, {
  background,
  text,
  closeBtn,
}) {
  const tooltipStyle = tooltip.style;
  if (background) tooltipStyle.setProperty(`--${tooltipClassName}-background`, background);
  if (text) tooltipStyle.setProperty(`--${tooltipClassName}-color`, text);
  if (closeBtn) tooltipStyle.setProperty(`--${tooltipClassName}-close-color`, closeBtn);
}

/**
 * @typedef {object} TooltipOptions
 * @property {string} type - The type of tooltip to create.
 * @property {TooltipColor} [color] - The color of tooltip to create.
 * @property {string} [title] - The title of tooltip to create.
 * @property {string} [text] - The text of tooltip to create.
 * @property {string} [button] - The button text of tooltip to create.
 * @property {boolean} [showCloseBtn=false] - True if the close button is shown in tooltip.
 * @property {string} [position='default'] - The modifier assigned to tooltip.
 * @property {boolean} [saveSession=false] - True if save session is active in tooltip.
 * @property {boolean} [clickClosure=false] - True if the tooltip is closed when clicked.
 */

/**
 * It creates a tooltip object and insert in DOM
 * @param {TooltipOptions} options - The options of tooltip to create.
 * @returns {HTMLElement} tooltip HTML tags
 */
function addTooltip({
  type,
  color,
  title,
  text,
  button,
  showCloseBtn = false,
  position,
}) {
  const tooltip = document.querySelector(`[data-tooltip-type="${type}"]`) || document.createElement('div');
  tooltip.classList.add(
    tooltipClassName,
    `${tooltipClassName}--${position || 'default'}`,
    hideClass,
  );
  if (text) {
    tooltip.innerHTML = '';
    // Title
    if (title) {
      const titleElement = document.createElement('span');
      titleElement.classList.add(tooltipTitleClassName);
      titleElement.innerHTML = title;
      tooltip.appendChild(titleElement);
    }
    // Text
    const textElement = document.createElement('p');
    textElement.classList.add(tooltipTextClassName);
    textElement.innerHTML = text;
    tooltip.appendChild(textElement);
    // Button
    if (button) {
      const buttonElement = document.createElement('button');
      buttonElement.classList.add(
        'btn',
        tooltipButtonClassName,
      );
      buttonElement.innerHTML = button;
      tooltip.appendChild(buttonElement);
    }
    // Close Btn
    if (showCloseBtn) {
      const closeBtn = document.createElement('button');
      closeBtn.classList.add(tooltipBtnCloseClassName);
      const spanBtnClose = document.createElement('span');
      spanBtnClose.classList.add('sr-only');
      closeBtn.appendChild(spanBtnClose);
      tooltip.appendChild(closeBtn);
    }
  }

  // Set Tooltip Colors
  if (color) setTooltipColor(tooltip, color);

  // if it is a new Element add to DOM
  const navbarHeader = document.querySelector('.navbar-header');
  if (navbarHeader && !tooltip.dataset.tooltipType && type) {
    tooltip.dataset.tooltipType = type;
    navbarHeader.appendChild(tooltip);
  }
  return tooltip;
}

/**
 * It creates a tooltip object with a show and hide method
 * @param {TooltipOptions} options - The type of tooltip you want to create.
 * options.type
 * @returns {Object} An object with several properties.
 */
function createTooltip(options) {
  const isTooltipCookie = options?.cookieExpiredType;
  const verifyTooltipInCookie = () => !isTooltipCookie
    || shouldShowTooltipCookie(options);
  if (!verifyTooltipInCookie()) return null;

  const isTooltipSession = options?.type && options?.saveSession;
  const verifyTooltipInSession = () =>
    !isTooltipSession || shouldShowTooltip(options?.type);
  if (!verifyTooltipInSession()) return null;

  const tooltip = addTooltip(options);
  const closeBtn = getCloseBtn(tooltip);
  const titleElement = getTitleContainer(tooltip);
  const textElement = getTextContainer(tooltip);
  const buttonElement = getButtonContainer(tooltip);

  /**
   * Show the tooltip
   */
  function show() {
    if (verifyTooltipInSession() || verifyTooltipInCookie()) {
      tooltip?.classList.remove(hideClass);
    }
  }

  /**
   * Hide the tooltip
   */
  function hide() {
    tooltip?.classList.add(hideClass);
    if (isTooltipSession) saveCloseInSession(options?.type);
    if (isTooltipCookie) saveTooltipCookie(options);
  }

  /**
   * Remove the tooltip when it closes by clicking the 'X'
   */
  function destroy() {
    closeBtn?.removeEventListener('click', closeEvent);
    tooltip?.remove();
  }

  /**
   * It hides and removes the tooltip
   * @param {Event} [e] - The event object.
   */
  function closeEvent(e) {
    e?.preventDefault();
    hide();
    destroy();
    // Send info to Analytics
    if (e?.currentTarget.parentElement.dataset.tooltipType === 'the-search-ai') {
      analyticsEventCenter.publish(
        analyticsEventCenter.EVENTS.searchTooltipLoaded,
        analyticsEventCenter.EVENTS.PAYLOADS.searchTooltipLoaded.item.cross,
      );
    }
  }

  const tooltipObject = {
    closeEvent,
    closeBtn,
    setTooltipColor: (color) => setTooltipColor(tooltip, color),
    show,
    hide,
    destroy,
    selector: tooltip,
    titleElement,
    textElement,
    buttonElement,
  };

  if (options.clickClosure) {
    handleTooltipDestruction(tooltipObject);
  } else {
    closeBtn?.addEventListener('click', closeEvent);
  }

  return tooltipObject;
}

/**
 * It creates a tooltip that shows up after a product is added to the cart, and it's only shown to
 * users who are not logged in
 * @returns {Object} An object with two methods: initTooltip and destroy.
 */
function createAccountTooltip() {
  const type = 'account';
  const tooltip = createTooltip({ type, position: type });

  /**
   * If the user is not logged in and the user has not closed the tooltip before, then show the tooltip
   * @returns {boolean} A boolean value.
   */
  function shouldShowTooltipAccount() {
    const userLoggedIn = gaInfo.login === 'logged';
    return shouldShowTooltip(type) && !userLoggedIn;
  }

  /**
   * If the tooltip should be shown, show it and add a click event listener to the close button
   */
  function show() {
    if (shouldShowTooltipAccount()) {
      tooltip.show();
      tooltip.closeBtn?.addEventListener('click', hide);
      // Account tooltip opens when add to cart not logged
      analyticsEventCenter.publish(
        analyticsEventCenter.EVENTS.loginTooltipOpened
      );
    }
  }

  /**
   * It hides the tooltip
   * If the event target is the tooltip close button, it saves this state in sessionStorage
   * It removes the click event listener from the close button
   * @param {Event} [e] - The event object.
   */
  function hide(e) {
    tooltip.hide();
    if (e instanceof Event && e.target.isSameNode(tooltip.closeBtn)) {
      saveCloseInSession(type);
      analyticsEventCenter.publish(
        analyticsEventCenter.EVENTS.loginTooltipClosed
      );
    }
    tooltip.closeBtn?.removeEventListener('click', hide);
  }

  /**
   * Initialize the functionality using analytics events to detect the actions
   * which trigger show and hide.
   */
  function initTooltip() {
    analyticsEventCenter.subscribe(
      analyticsEventCenter.EVENTS.productAddedToCart,
      show,
    );
    analyticsEventCenter.subscribe(
      analyticsEventCenter.EVENTS.userWillNavigateDinamically,
      hide,
    );
  }

  /**
   * Destroy the component and free memory unsubscribing from the related events
   */
  function destroy() {
    analyticsEventCenter.unsubscribe(
      analyticsEventCenter.EVENTS.productAddedToCart,
      show,
    );
    analyticsEventCenter.unsubscribe(
      analyticsEventCenter.EVENTS.userWillNavigateDinamically,
      hide,
    );
  }

  return {
    initTooltip,
    destroy,
  };
}

/**
 * Handles document click and touchstart listeners
 * to remove Tooltip
 * @param {Object} tooltip Tooltip object
 */
function handleTooltipDestruction(tooltip) {
  currentTooltip = tooltip;
  document.addEventListener('click', destroyOnClick);
  document.addEventListener('touchstart', destroyOnClick);
}

/**
 * Destroys current tooltip and removes document listeners
 */
function destroyOnClick() {
  currentTooltip?.destroy();
  document.removeEventListener('click', destroyOnClick);
  document.removeEventListener('touchstart', destroyOnClick);
  currentTooltip = null;
}

/**
 * It creates a tooltip, initializes it, and then destroys it
 * when the page is unloaded.
 */
function init() {
  const accountTooltip = createAccountTooltip();
  accountTooltip.initTooltip();

  onOnce(window, 'beforeunload', () => {
    accountTooltip.destroy();
  });
}

module.exports = {
  createTooltip,
  init,
  tooltipClassName,
};
